import React from 'react';
import { ChevronDown, Sparkles, Shield, Wrench } from 'lucide-react';

interface HeroProps {
  activeBrand: string;
  brandName: string;
}

export const Hero: React.FC<HeroProps> = ({ activeBrand, brandName }) => {
  const scrollToGallery = () => {
    const gallery = document.getElementById('gallery');
    if (gallery) {
      gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const brandDescriptions: Record<string, string> = {
    used: 'Curated selection of pre-owned motorcycles, each inspected and ready to ride.',
    honda: 'Authorized Honda dealer offering the latest models with full factory warranty.',
    hero: 'Hero motorcycles - reliable, efficient, and built for African roads.',
    sym: 'SYM scooters and motorcycles - European engineering, Asian value.',
    kayo: 'Kayo dirt bikes and ATVs - off-road performance at unbeatable prices.',
    bajaj: "Bajaj - world's third largest motorcycle manufacturer, now in South Africa.",
    lifan: 'Lifan - innovative engines and modern designs for every rider.',
    other: 'Other motorcycle brands and models available.',
    stripping: 'Bike stripping and salvage for spare parts.',
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 lg:pt-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-dark-bg via-dark-bg to-neutral-950/50" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/5 via-transparent to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 pt-32 lg:pt-40">
        <div className="text-center animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 text-gold px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest mb-6">
            <Sparkles className="h-3 w-3" />
            {brandName} Collection
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter text-white mb-6 leading-tight">
            {brandName}
            <br />
            <span className="text-gold font-extrabold">Showroom</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-neutral-400 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
            {brandDescriptions[activeBrand] || 'Explore our premium motorcycle collection.'}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={scrollToGallery}
              className="group bg-gold text-dark-bg font-bold text-sm sm:text-base uppercase tracking-widest px-8 py-4 rounded-xl hover:bg-gold-hover transition-all shadow-lg shadow-gold/20 flex items-center justify-center gap-2"
            >
              View Inventory
              <ChevronDown className="h-5 w-5 transition-transform group-hover:translate-y-1" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto text-center">
            <div className="p-4 bg-dark-card/50 border border-dark-border rounded-2xl hover:border-gold/20 transition-colors">
              <Shield className="h-6 w-6 text-gold mx-auto mb-2" />
              <p className="text-xs font-bold text-white uppercase tracking-wide">Quality Assured</p>
              <p className="text-[11px] text-neutral-500 mt-1">Every bike inspected</p>
            </div>
            <div className="p-4 bg-dark-card/50 border border-dark-border rounded-2xl hover:border-gold/20 transition-colors">
              <Wrench className="h-6 w-6 text-gold mx-auto mb-2" />
              <p className="text-xs font-bold text-white uppercase tracking-wide">Workshop Ready</p>
              <p className="text-[11px] text-neutral-500 mt-1">Full service center</p>
            </div>
            <div className="p-4 bg-dark-card/50 border border-dark-border rounded-2xl hover:border-gold/20 transition-colors">
              <Sparkles className="h-6 w-6 text-gold mx-auto mb-2" />
              <p className="text-xs font-bold text-white uppercase tracking-wide">Finance Available</p>
              <p className="text-[11px] text-neutral-500 mt-1">Flexible payment plans</p>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={scrollToGallery}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-neutral-500 hover:text-gold transition-colors lg:hidden"
        aria-label="Scroll to gallery"
      >
        <ChevronDown className="h-8 w-8" />
      </button>
    </section>
  );
};